import { KICK, type GameAction, type InputFrame, type Player, type Vec2 } from "@golazo/shared";
import { normalize, vec } from "../core/math.js";
import { t } from "../i18n/index.js";
import { haptic } from "../mobile/haptics.js";
import type { InputSource } from "./keyboard.js";
import {
  followStick,
  layoutTouch,
  onTouchPrefsChange,
  readSafeInsets,
  touchPrefs,
  type TouchPrefs,
} from "./touchLayout.js";

/**
 * El mando tactil.
 *
 * Tres formas de golpear, y ninguna obliga a dejar de correr:
 *
 * 1. **Tocar el campo.** Con el balon, pones el dedo donde quieres que vaya
 *    —un compañero, el hueco, la escuadra— y va ahi: un toque pasa, mantener
 *    carga el tiro, arrastrar mueve la diana. Es el clic del raton hecho dedo,
 *    y como el, sin iman: el balon va a donde tocas, no al compañero mas
 *    cercano. Sin balon el toque no hace nada (nada de entradas sin querer).
 * 2. **El boton ambar**, como siempre: toque pasa, mantener tira, y arrastrar
 *    ESE MISMO dedo apunta.
 * 3. El joystick sigue valiendo para apuntar, para quien ya jugaba asi.
 *
 * Lo que no se ve se siente: la carga dibuja un anillo alrededor del boton y
 * de la diana (blanco = pase, ambar = tiro, oro = punto perfecto) y el movil
 * vibra al cruzar cada tramo. Ver `mobile/haptics.ts`.
 *
 * Todo esto es input: acaba en el mismo `InputFrame` que el teclado (`move` +
 * `kick`), asi que ni la simulacion ni la red saben que existe.
 */
const STICK_RADIUS = 56;
const STICK_DEAD = 0.14;

/*
 * Zona muerta de la mira del boton, en pixeles de dedo.
 *
 * Era 14 y se notaba como retraso: hasta que el pulgar no recorria 14 px no
 * pasaba nada. 6 es el limite por debajo del cual se cuela el rodamiento
 * natural del pulgar al mantener pulsado; para que no tiemble, la direccion se
 * suaviza (`AIM_SMOOTH`).
 */
const AIM_DEAD_PX = 6;
/** Suavizado de la mira del boton: 0 = instantanea y nerviosa, 1 = pastosa. */
const AIM_SMOOTH = 0.45;
/** Lo que el boton se deja arrastrar hacia la mira, como pista de que esta viva. */
const AIM_NUB_PX = 18;
/**
 * Cuanto se recuerda un toque que cayo mientras controlabas el balon.
 *
 * Al recibir, el jugador tarda `POSSESSION.TRAP_TIME` (0,08-0,16 s) en
 * hacerse con el balon, y un golpeo pulsado en ese rato no cuenta. Con el
 * teclado casi no pasa; con el dedo es lo normal —el balon llega y tocas al
 * compañero—, y un toque que no responde es lo peor que puede hacer un mando
 * tactil. Asi que el mando se lo guarda y lo juega en cuanto el balon es tuyo.
 * Mas de un cuarto de segundo ya no es "a la vez que recibo": se olvida.
 */
const TAP_MEMORY_MS = 260;

export interface TouchFieldOptions {
  /** Punto de la ventana -> punto del campo (coordenadas de la simulacion). */
  toWorld(clientX: number, clientY: number, canvas: HTMLCanvasElement): Vec2 | null;
  /** Punto del campo -> ventana, para dibujar la diana donde esta. */
  toScreen(x: number, y: number, canvas: HTMLCanvasElement): Vec2 | null;
  /** El jugador que llevas: se golpea desde el. */
  player(): Player | null;
  /** Con el balon, o con un remate de primera a tiro. Sin eso, tocar no golpea. */
  canStrike(): boolean;
  /** El balon es tuyo y ya lo controlas (no lo estas recibiendo). */
  ballReady(): boolean;
}

export interface TouchControlsOptions {
  root?: HTMLElement;
  /** Sin esto no se puede tocar el campo (tests, o partidos sin lienzo). */
  field?: TouchFieldOptions;
  /** Pulsar o soltar cambia el fotograma: en red se envia al momento. */
  onChange?: () => void;
}

export interface TouchControls extends InputSource {
  readonly element: HTMLElement;
  show(): void;
  hide(): void;
  /**
   * Enciende los botones que estan activos AHORA en el juego, vengan del dedo
   * o del teclado. Sin esto la botonera miente —dice "no estas golpeando"
   * mientras el balon sale disparado.
   */
  reflect(held: ReadonlySet<GameAction>): void;
  /**
   * Una vez por frame: la carga del golpeo de tu jugador (0-1), o `null` si no
   * esta cargando. La de la SIMULACION, no un reloj propio: el anillo cuenta
   * lo que va a salir, no lo que el dedo cree.
   */
  sync(charge: number | null): void;
}

/** Un anillo de carga: pista, tramo perfecto, relleno y la muesca pase/tiro. */
function ringSvg(): string {
  const notch = KICK.PASS_MAX_CHARGE * 360;
  const band = (1 - KICK.PERFECT_CHARGE) * 100;
  return `
    <svg class="golazo-ring" viewBox="-50 -50 100 100" aria-hidden="true" focusable="false">
      <circle class="golazo-ring__track" r="45" />
      <circle class="golazo-ring__band" r="45" pathLength="100" transform="rotate(-90)"
        stroke-dasharray="${band} ${100 - band}" stroke-dashoffset="${-(100 - band)}" />
      <circle class="golazo-ring__fill" r="45" pathLength="100" transform="rotate(-90)" stroke-dasharray="0 100" />
      <line class="golazo-ring__notch" x1="0" y1="-50" x2="0" y2="-39" transform="rotate(${notch})" />
      <path class="golazo-ring__aim" d="M -7 -50 L 0 -60 L 7 -50 Z" />
    </svg>`;
}

/** Un balon: se reconoce de reojo sin leer nada. */
export const KICK_GLYPH = `
  <svg class="golazo-touch__glyph" viewBox="-12 -12 24 24" aria-hidden="true" focusable="false">
    <circle r="9.5" fill="none" stroke="currentColor" stroke-width="2" />
    <path d="M0 -4.2 L4 -1.3 L2.5 3.4 L-2.5 3.4 L-4 -1.3 Z" fill="currentColor" />
    <path d="M0 -4.2 V-9.5 M4 -1.3 L9 -3 M2.5 3.4 L5.6 7.6 M-2.5 3.4 L-5.6 7.6 M-4 -1.3 L-9 -3"
      stroke="currentColor" stroke-width="1.6" fill="none" />
  </svg>`;

/** Dos flechas: acelerar. */
export const DASH_GLYPH = `
  <svg class="golazo-touch__glyph" viewBox="-12 -12 24 24" aria-hidden="true" focusable="false">
    <path d="M-8 -7 L-1 0 L-8 7 M1 -7 L8 0 L1 7" fill="none" stroke="currentColor"
      stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" />
  </svg>`;

export function createTouchControls(options: TouchControlsOptions = {}): TouchControls {
  const root = options.root ?? document.body;
  const field = options.field ?? null;

  const shell = document.createElement("div");
  shell.className = "golazo-touch is-hidden";
  shell.innerHTML = `
    <div class="golazo-touch__stick-zone" data-stick-zone>
      <div class="golazo-touch__stick-base" data-stick-base hidden>
        <i class="golazo-touch__stick-dir" data-stick-dir></i>
        <div class="golazo-touch__stick-knob" data-stick-knob></div>
      </div>
    </div>
    <div class="golazo-touch__reticle" data-reticle hidden>${ringSvg()}<i class="golazo-touch__reticle-dot"></i></div>
    <div class="golazo-touch__actions" data-actions>
      <button type="button" class="golazo-touch__btn golazo-touch__btn--kick" data-action="kick" aria-label="${t("Golpear")}">
        ${ringSvg()}${KICK_GLYPH}
      </button>
      <button type="button" class="golazo-touch__btn golazo-touch__btn--dash" data-action="dash" aria-label="${t("Correr")}">
        ${DASH_GLYPH}
      </button>
    </div>
  `;
  root.appendChild(shell);

  const stickZone = shell.querySelector<HTMLElement>("[data-stick-zone]")!;
  const stickBase = shell.querySelector<HTMLElement>("[data-stick-base]")!;
  const stickKnob = shell.querySelector<HTMLElement>("[data-stick-knob]")!;
  const actionsEl = shell.querySelector<HTMLElement>("[data-actions]")!;
  const kickBtn = shell.querySelector<HTMLElement>('[data-action="kick"]')!;
  const dashBtn = shell.querySelector<HTMLElement>('[data-action="dash"]')!;
  const reticle = shell.querySelector<HTMLElement>("[data-reticle]")!;
  const rings = [...shell.querySelectorAll<SVGElement>(".golazo-ring")];

  const held = new Set<GameAction>();
  const pressed = new Set<GameAction>();
  const move = vec(0, 0);
  /**
   * Que dedo mantiene cada boton. Con dos pulgares —que es como se juega—
   * levantar uno no puede soltar lo que sujeta el otro.
   */
  const buttonPointers = new Map<number, GameAction>();

  let prefs: TouchPrefs = touchPrefs();

  // ---- Colocacion -------------------------------------------------------

  const place = (): void => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const layout = layoutTouch(width, height, prefs, readSafeInsets());
    for (const [el, box] of [
      [kickBtn, layout.kick],
      [dashBtn, layout.dash],
    ] as const) {
      el.style.setProperty("--size", `${box.size}px`);
      el.style.left = `${box.cx - box.size / 2}px`;
      el.style.top = `${box.cy - box.size / 2}px`;
    }
    stickZone.style.left = `${layout.stickFrom}px`;
    stickZone.style.width = `${layout.stickTo - layout.stickFrom}px`;
    shell.classList.toggle("is-left-handed", prefs.leftHanded);
  };
  place();
  const unsubPrefs = onTouchPrefsChange((next) => {
    prefs = next;
    place();
  });
  window.addEventListener("resize", place);
  window.visualViewport?.addEventListener("resize", place);

  // ---- Joystick ---------------------------------------------------------

  let stickPointerId: number | null = null;
  let stickAnchorX = 0;
  let stickAnchorY = 0;

  const clearStick = (): void => {
    stickPointerId = null;
    stickBase.hidden = true;
    stickBase.classList.remove("is-moving");
    move.x = 0;
    move.y = 0;
  };

  const drawStick = (): void => {
    stickBase.style.left = `${stickAnchorX}px`;
    stickBase.style.top = `${stickAnchorY}px`;
  };

  const updateStick = (clientX: number, clientY: number): void => {
    const s = followStick(stickAnchorX, stickAnchorY, clientX, clientY, STICK_RADIUS, STICK_DEAD);
    stickAnchorX = s.anchorX;
    stickAnchorY = s.anchorY;
    move.x = s.moveX;
    move.y = s.moveY;
    drawStick();
    stickKnob.style.transform = `translate(${s.knobX}px, ${s.knobY}px)`;
    const moving = s.moveX !== 0 || s.moveY !== 0;
    stickBase.classList.toggle("is-moving", moving);
    if (moving) stickBase.style.setProperty("--dir", `${Math.atan2(s.moveY, s.moveX) + Math.PI / 2}rad`);
  };

  const onStickDown = (event: PointerEvent): void => {
    if (stickPointerId !== null) return;
    if (event.pointerType === "mouse" && event.buttons !== 1) return;
    stickPointerId = event.pointerId;
    stickAnchorX = event.clientX;
    stickAnchorY = event.clientY;
    stickBase.hidden = false;
    drawStick();
    stickKnob.style.transform = "translate(0, 0)";
    try {
      stickZone.setPointerCapture(event.pointerId);
    } catch {
      /* puntero ya suelto */
    }
    shell.dispatchEvent(new CustomEvent("golazo-touch:stick"));
    event.preventDefault();
  };

  const onStickMove = (event: PointerEvent): void => {
    if (event.pointerId !== stickPointerId) return;
    updateStick(event.clientX, event.clientY);
    event.preventDefault();
  };

  const onStickUp = (event: PointerEvent): void => {
    if (event.pointerId !== stickPointerId) return;
    try {
      stickZone.releasePointerCapture(event.pointerId);
    } catch {
      /* ya suelto */
    }
    clearStick();
    event.preventDefault();
  };

  // ---- Carga (anillos) --------------------------------------------------

  /** La ultima carga que conto la simulacion; `null` si no se carga. */
  let lastCharge: number | null = null;

  /*
   * El golpeo que ha pedido el dedo (boton o campo), para no perderlo si cae
   * mientras recibes. `withBall`: se pidio pudiendo golpear (sin eso es una
   * entrada, y una entrada no se repite). `acked`: la simulacion ya carga.
   */
  let strike: { withBall: boolean; acked: boolean } | null = null;
  /** Un toque que se trago el control del balon: se juega cuando se pueda. */
  let tapUntil = 0;
  let tapFromField = false;
  const tapTarget = vec(0, 0);
  /** Este tick se ha pulsado de mentira (el toque recordado): se suelta en el siguiente. */
  let syntheticKick = false;

  const beginStrike = (): void => {
    strike = { withBall: field?.canStrike() ?? false, acked: false };
  };

  /** Al soltar: si la simulacion no llego a cargar porque estabas recibiendo, se recuerda. */
  const endStrike = (fromField: boolean): void => {
    if (strike && strike.withBall && !strike.acked && field && !field.ballReady()) {
      tapUntil = performance.now() + TAP_MEMORY_MS;
      tapFromField = fromField;
      tapTarget.x = fieldTarget.x;
      tapTarget.y = fieldTarget.y;
    }
    strike = null;
  };

  const paintRings = (charge: number | null): void => {
    const c = charge ?? 0;
    const state = charge === null ? "" : c >= KICK.PERFECT_CHARGE ? "is-perfect" : c > KICK.PASS_MAX_CHARGE ? "is-shot" : "is-pass";
    for (const ring of rings) {
      ring.querySelector(".golazo-ring__fill")?.setAttribute("stroke-dasharray", `${(c * 100).toFixed(1)} 100`);
      ring.classList.remove("is-pass", "is-shot", "is-perfect");
      if (state) ring.classList.add(state);
    }
    kickBtn.classList.toggle("is-charging", charge !== null);
  };

  // ---- Boton de golpeo: mantener y arrastrar apunta ---------------------

  const aim = vec(0, 0);
  let aiming = false;
  let aimPointerId: number | null = null;
  let aimOriginX = 0;
  let aimOriginY = 0;

  const clearAim = (): void => {
    aiming = false;
    aimPointerId = null;
    aim.x = 0;
    aim.y = 0;
    kickBtn.style.removeProperty("--aim-x");
    kickBtn.style.removeProperty("--aim-y");
    kickBtn.classList.remove("is-aiming");
  };

  const onButtonDown = (event: PointerEvent): void => {
    const target = (event.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (!target) return;
    const action = target.dataset.action as GameAction | undefined;
    if (!action) return;
    if (held.has(action)) return;
    held.add(action);
    pressed.add(action);
    buttonPointers.set(event.pointerId, action);
    if (action === "kick") {
      beginStrike();
      // El origen es donde ha caido el dedo, no el centro del boton: la mira
      // se mide contra donde el pulgar cree que esta.
      aimPointerId = event.pointerId;
      aimOriginX = event.clientX;
      aimOriginY = event.clientY;
      aiming = false;
    }
    // Capturar el puntero garantiza que el `pointerup` llegue a ESTE boton por
    // muy lejos que se vaya el dedo mientras carga.
    try {
      target.setPointerCapture(event.pointerId);
    } catch {
      /* puntero ya suelto */
    }
    target.classList.add("is-pressed");
    haptic("press");
    options.onChange?.();
    event.preventDefault();
  };

  /**
   * Arrastre sobre el boton de golpeo: la mira. La zona muerta separa "he
   * tocado para pasar" de "estoy apuntando"; volver al centro la suelta y el
   * joystick recupera el mando, sin levantar el dedo ni perder la carga.
   */
  const onButtonMove = (event: PointerEvent): void => {
    if (event.pointerId !== aimPointerId) return;
    const dx = event.clientX - aimOriginX;
    const dy = event.clientY - aimOriginY;
    const dist = Math.hypot(dx, dy);
    if (dist < AIM_DEAD_PX) {
      if (aiming) {
        aiming = false;
        aim.x = 0;
        aim.y = 0;
        kickBtn.classList.remove("is-aiming");
      }
      event.preventDefault();
      return;
    }
    const nx = dx / dist;
    const ny = dy / dist;
    if (!aiming) {
      // El primer frame entra en crudo: suavizar la ENTRADA se sentiria como retraso.
      aiming = true;
      aim.x = nx;
      aim.y = ny;
    } else {
      aim.x += (nx - aim.x) * AIM_SMOOTH;
      aim.y += (ny - aim.y) * AIM_SMOOTH;
      normalize(aim);
    }
    const nub = Math.min(dist, AIM_NUB_PX);
    kickBtn.style.setProperty("--aim-x", `${aim.x * nub}px`);
    kickBtn.style.setProperty("--aim-y", `${aim.y * nub}px`);
    kickBtn.style.setProperty("--aim-angle", `${Math.atan2(aim.y, aim.x) + Math.PI / 2}rad`);
    kickBtn.classList.add("is-aiming");
    event.preventDefault();
  };

  /**
   * Suelta el boton que tenia ese dedo. Por `pointerId`, no por lo que haya
   * debajo: al cargar, la yema rueda y puede acabar fuera del boton.
   */
  const releasePointer = (event: PointerEvent): void => {
    const action = buttonPointers.get(event.pointerId);
    if (action === undefined) return;
    buttonPointers.delete(event.pointerId);
    if (event.pointerId === aimPointerId) clearAim();
    held.delete(action);
    for (const el of shell.querySelectorAll<HTMLElement>("[data-action]")) {
      if (el.dataset.action === action) el.classList.remove("is-pressed");
    }
    if (action === "kick") {
      if (lastCharge !== null) haptic("release", lastCharge);
      endStrike(false);
    }
    options.onChange?.();
    event.preventDefault();
  };

  // ---- Tocar el campo ---------------------------------------------------

  let fieldPointer: number | null = null;
  let fieldCanvas: HTMLCanvasElement | null = null;
  /** La diana, en coordenadas del CAMPO: si la camara se mueve, se queda en el cesped. */
  const fieldTarget = vec(0, 0);
  let fieldClientX = 0;
  let fieldClientY = 0;
  const fieldMove = vec(0, 0);

  const ripple = (x: number, y: number, live: boolean): void => {
    const el = document.createElement("i");
    el.className = `golazo-touch__ripple${live ? " is-live" : ""}`;
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    shell.appendChild(el);
    el.addEventListener("animationend", () => el.remove());
    // Por si la animacion no corre (movimiento reducido, pestaña oculta).
    setTimeout(() => el.remove(), 700);
  };

  const endFieldStrike = (): void => {
    if (fieldPointer === null) return;
    fieldPointer = null;
    fieldCanvas = null;
    held.delete("kick");
    reticle.hidden = true;
    kickBtn.classList.remove("is-remote");
  };

  const onFieldDown = (event: PointerEvent): void => {
    if (!field || !prefs.fieldTouch || shell.classList.contains("is-hidden")) return;
    if (event.pointerType === "mouse") return;
    const canvas = event.target;
    if (!(canvas instanceof HTMLCanvasElement)) return;
    // Un solo golpeo a la vez: si el boton ya carga, este dedo no cuenta.
    if (fieldPointer !== null || held.has("kick")) return;
    const live = field.canStrike();
    ripple(event.clientX, event.clientY, live);
    if (!live) return;
    const at = field.toWorld(event.clientX, event.clientY, canvas);
    if (!at) return;
    fieldPointer = event.pointerId;
    fieldCanvas = canvas;
    fieldTarget.x = at.x;
    fieldTarget.y = at.y;
    fieldClientX = event.clientX;
    fieldClientY = event.clientY;
    held.add("kick");
    pressed.add("kick");
    beginStrike();
    kickBtn.classList.add("is-remote");
    try {
      canvas.setPointerCapture(event.pointerId);
    } catch {
      /* puntero ya suelto */
    }
    haptic("press");
    options.onChange?.();
    event.preventDefault();
  };

  /**
   * Arrastrar mueve la diana lo que se mueve el dedo, medido en el campo. No
   * se recalcula "lo que hay debajo del dedo": con la camara siguiendote, eso
   * arrastraria la diana con la camara aunque el dedo no se moviera.
   */
  const onFieldMove = (event: PointerEvent): void => {
    if (event.pointerId !== fieldPointer || !field || !fieldCanvas) return;
    const before = field.toWorld(fieldClientX, fieldClientY, fieldCanvas);
    const after = field.toWorld(event.clientX, event.clientY, fieldCanvas);
    if (before && after) {
      fieldTarget.x += after.x - before.x;
      fieldTarget.y += after.y - before.y;
    }
    fieldClientX = event.clientX;
    fieldClientY = event.clientY;
    event.preventDefault();
  };

  const onFieldUp = (event: PointerEvent): void => {
    if (event.pointerId !== fieldPointer) return;
    const charge = lastCharge;
    endStrike(true);
    endFieldStrike();
    if (charge !== null) haptic("release", charge);
    options.onChange?.();
    event.preventDefault();
  };

  // ---- Todo fuera ---------------------------------------------------------

  const onBlur = (): void => {
    held.clear();
    pressed.clear();
    buttonPointers.clear();
    clearStick();
    clearAim();
    endFieldStrike();
    strike = null;
    tapUntil = 0;
    syntheticKick = false;
    for (const el of shell.querySelectorAll(".golazo-touch__btn")) el.classList.remove("is-pressed", "is-lit");
  };

  stickZone.addEventListener("pointerdown", onStickDown);
  stickZone.addEventListener("pointermove", onStickMove);
  stickZone.addEventListener("pointerup", onStickUp);
  stickZone.addEventListener("pointercancel", onStickUp);

  actionsEl.addEventListener("pointerdown", onButtonDown);
  actionsEl.addEventListener("pointermove", onButtonMove);
  actionsEl.addEventListener("pointerup", releasePointer);
  /*
   * `pointercancel` si; `pointerleave` NO. `pointerleave` soltaba el boton en
   * cuanto la yema cruzaba el borde —y al cargar SIEMPRE deriva—: "aprieto y
   * se suelta solo". Con el puntero capturado el `pointerup` llega igual.
   */
  actionsEl.addEventListener("pointercancel", releasePointer);

  window.addEventListener("pointerdown", onFieldDown);
  window.addEventListener("pointermove", onFieldMove);
  window.addEventListener("pointerup", onFieldUp);
  window.addEventListener("pointercancel", onFieldUp);
  window.addEventListener("blur", onBlur);

  return {
    element: shell,
    reflect(activeHeld: ReadonlySet<GameAction>): void {
      for (const el of shell.querySelectorAll<HTMLElement>("[data-action]")) {
        const action = el.dataset.action as GameAction | undefined;
        el.classList.toggle("is-lit", action !== undefined && activeHeld.has(action));
      }
    },
    sync(charge: number | null): void {
      const prev = lastCharge;
      if (charge !== null && prev !== null) {
        if (prev <= KICK.PASS_MAX_CHARGE && charge > KICK.PASS_MAX_CHARGE) haptic("threshold");
        if (prev < KICK.PERFECT_CHARGE && charge >= KICK.PERFECT_CHARGE) haptic("perfect");
      }
      lastCharge = charge;
      if (strike && charge !== null) strike.acked = true;
      paintRings(charge);
      if (fieldPointer !== null && field && fieldCanvas) {
        const at = field.toScreen(fieldTarget.x, fieldTarget.y, fieldCanvas);
        if (at) {
          reticle.hidden = false;
          reticle.style.transform = `translate(${at.x}px, ${at.y}px)`;
        }
      } else if (!reticle.hidden) {
        reticle.hidden = true;
      }
    },
    show(): void {
      shell.classList.remove("is-hidden");
      place();
    },
    hide(): void {
      shell.classList.add("is-hidden");
      onBlur();
    },
    consume(): InputFrame {
      const kickFinger = fieldPointer !== null || [...buttonPointers.values()].includes("kick");
      if (syntheticKick) {
        syntheticKick = false;
        if (!kickFinger) held.delete("kick");
      }
      const ready = field?.ballReady() ?? false;
      // Mantenido a traves del control: en cuanto el balon es tuyo, empieza la carga.
      if (strike && strike.withBall && !strike.acked && kickFinger && ready) pressed.add("kick");
      // Y el toque que ya se solto, se juega ahora: un tick pulsado y fuera.
      if (tapUntil > 0) {
        if (performance.now() > tapUntil || kickFinger) {
          tapUntil = 0;
        } else if (ready) {
          tapUntil = 0;
          pressed.add("kick");
          held.add("kick");
          syntheticKick = true;
          const me = tapFromField ? field?.player() : null;
          if (me) {
            const dx = tapTarget.x - me.pos.x;
            const dy = tapTarget.y - me.pos.y;
            const len = Math.hypot(dx, dy);
            if (len > 1) {
              fieldMove.x = dx / len;
              fieldMove.y = dy / len;
              return { move: fieldMove, held, pressed };
            }
          }
        }
      }
      /*
       * Tocando el campo, `move` es la direccion a la diana; arrastrando el
       * boton, la de la mira. Se sustituye `move` en vez de añadir un campo al
       * `InputFrame` porque la simulacion ya lo lee como mira mientras cargas
       * (`lockKickDirection`, `chargeSteer`): cargando no se acelera, asi que
       * no se le quita el sitio a ningun movimiento.
       */
      if (fieldPointer !== null && field) {
        const me = field.player();
        if (me) {
          const dx = fieldTarget.x - me.pos.x;
          const dy = fieldTarget.y - me.pos.y;
          const len = Math.hypot(dx, dy);
          if (len > 1) {
            fieldMove.x = dx / len;
            fieldMove.y = dy / len;
            return { move: fieldMove, held, pressed };
          }
        }
      }
      return { move: aiming ? aim : move, held, pressed };
    },
    endTick(): void {
      pressed.clear();
    },
    dispose(): void {
      stickZone.removeEventListener("pointerdown", onStickDown);
      stickZone.removeEventListener("pointermove", onStickMove);
      stickZone.removeEventListener("pointerup", onStickUp);
      stickZone.removeEventListener("pointercancel", onStickUp);
      actionsEl.removeEventListener("pointerdown", onButtonDown);
      actionsEl.removeEventListener("pointermove", onButtonMove);
      actionsEl.removeEventListener("pointerup", releasePointer);
      actionsEl.removeEventListener("pointercancel", releasePointer);
      window.removeEventListener("pointerdown", onFieldDown);
      window.removeEventListener("pointermove", onFieldMove);
      window.removeEventListener("pointerup", onFieldUp);
      window.removeEventListener("pointercancel", onFieldUp);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("resize", place);
      unsubPrefs();
      shell.remove();
      held.clear();
      pressed.clear();
      clearStick();
      clearAim();
    },
  };
}
